import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UserEditModal = ({ user, onClose, onSave }) => {
  const [selectedRoles, setSelectedRoles] = useState(user?.roles || []);
  const [saving, setSaving] = useState(false);

  const availableRoles = [
    { value: 'admin', label: 'Admin', description: 'Full platform access and control' },
    { value: 'chair', label: 'Chair', description: 'Conference chair with decision authority' },
    { value: 'organizer', label: 'Organizer', description: 'Conference organizer and coordinator' },
    { value: 'reviewer', label: 'Reviewer', description: 'Paper reviewer and evaluator' },
    { value: 'author', label: 'Author', description: 'Paper author and submitter' }
  ];

  const handleToggleRole = (role) => {
    if (selectedRoles?.includes(role)) {
      setSelectedRoles(prev => prev?.filter(r => r !== role));
    } else {
      setSelectedRoles(prev => [...prev, role]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(user?.id, { roles: selectedRoles });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border-2 border-border rounded-lg shadow-elevation-4 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b-2 border-border flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-1">Edit User Roles</h2>
            <p className="text-sm text-muted-foreground">
              {user?.firstName} {user?.lastName} ({user?.email})
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={24} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Assign Roles</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Select one or more roles for this user. Each role grants specific permissions and access levels.
            </p>
          </div>

          <div className="space-y-3">
            {availableRoles?.map((role) => (
              <div
                key={role?.value}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                  selectedRoles?.includes(role?.value)
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/30'
                }`}
                onClick={() => handleToggleRole(role?.value)}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    <div
                      className={`w-5 h-5 border-2 flex items-center justify-center ${
                        selectedRoles?.includes(role?.value)
                          ? 'bg-primary border-primary' :'border-input bg-background'
                      }`}
                    >
                      {selectedRoles?.includes(role?.value) && (
                        <Icon name="Check" size={14} className="text-white" />
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-foreground mb-1">{role?.label}</div>
                    <div className="text-sm text-muted-foreground">{role?.description}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {selectedRoles?.length === 0 && (
            <div className="mt-4 p-4 bg-warning/10 border-2 border-warning rounded-lg flex items-start gap-3">
              <Icon name="AlertTriangle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-warning font-medium">No roles selected</p>
                <p className="text-sm text-warning/80 mt-1">
                  User must have at least one role to access the platform.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t-2 border-border flex items-center justify-end gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            loading={saving}
            disabled={selectedRoles?.length === 0}
            iconName="Save"
            iconPosition="left"
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserEditModal;